/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import FilterModal from "./FilterModal";
import type { ApiResponse, FilterCondition, FilterQuery } from "./FilterModal";
import IconComponent from "@CubeComponents/icons";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getConditionMet, callRemoteSource, type FilterType, type QueryOperator } from "../filterUtils";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";

/* --------------------------------------------
   Types
   -------------------------------------------- */

export interface FilterColumn {
  /** Unique field key */
  field: string;
  /** Display name for the column */
  name: string;
  /** Filter type: "Text" | "Date" | "Number" | "MultiSelect" | "FileSize" */
  filterType: string;
  /** Unique id (defaults to field if not provided) */
  id?: string;
  /** Disable filtering for this column */
  disableFiltering?: boolean;
}

interface RemoteDataSourceConfig {
  /** API endpoint URL or a function that performs the remote call */
  url: string | ((body: unknown) => Promise<unknown>);
  /** Base query object to send with the remote call (e.g. { params: { ... }, skip: 0, take: 50 }) */
  query?: FilterQuery;
  /** Transform raw API response before it reaches the FilterModal (e.g. extract nested array) */
  beforeDataBound?: (data: unknown) => unknown[];
  /** Transform filter conditions before they are passed to onFilterChange (e.g. build a where clause) */
  getUpdatedFilterConditions?: (filterConditionsObj: Record<string, unknown>) => unknown;
}

/** A single saved-view filter condition keyed by field in the conditions object */
export interface FilterConditionEntry {
  id?: string;
  type?: string;
  option?: string;
  text?: unknown;
  logic?: string;
}

const extractFilterIds = (obj: Record<string, FilterConditionEntry>): string[] =>
  Object.values(obj)
    .map((c) => c?.id)
    .filter((id): id is string => Boolean(id));

/**
 * `TItem` is the caller's row type. It exists so `data` and `getFieldValue` agree: call sites
 * annotate their accessor with their own row type (e.g. the Gantt's `Task`), which no fixed
 * parameter type would accept.
 */
export interface FilterButtonProps<TItem = unknown> {
  /** Column definitions available for filtering */
  columns: FilterColumn[];
  /** The local dataset to filter against. Used for autocomplete suggestions in local mode. */
  data?: TItem[];
  /** Called whenever filter conditions change. Receives the conditions object keyed by field. */
  onFilterChange: (filterConditionsObject: Record<string, FilterConditionEntry>) => void;
  /** Called when filter predicates change (remote mode). Receives predicates for building API where clauses. */
  onFilterPredicatesChange?: (predicates: unknown) => void;
  /** Optional: custom function to extract a field value from a data item */
  getFieldValue?: (item: TItem, field: string) => unknown;
  /** Optional: render a custom trigger instead of the default filter icon button */
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    hasActiveFilters: boolean;
  }) => React.ReactNode;
  /** Optional: remote data source config. When provided, filter suggestions are fetched from the API. */
  remoteDataSource?: RemoteDataSourceConfig;
  /** Optional: ref to the grid instance (needed for remote filtering to refresh data) */
  gridRef?: React.RefObject<unknown>;
  /** Optional: seed the derived filterConditionsObject (used to restore filters from a saved view) */
  initialFilterConditionsObject?: Record<string, FilterConditionEntry>;
}

/* --------------------------------------------
   Component
   -------------------------------------------- */

export default function FilterButton<TItem = unknown>({
  columns,
  data = [],
  onFilterChange,
  onFilterPredicatesChange,
  getFieldValue,
  renderTrigger,
  remoteDataSource,
  gridRef: externalGridRef,
  initialFilterConditionsObject,
}: FilterButtonProps<TItem>) {
  const { t } = useTranslation();

  const isRemote = !!remoteDataSource;

  // FilterModal's row type declares `field` / `operator` as required. A saved-view entry can be
  // missing either (the modal itself guards with `if (!condition.field)`), so the mismatch is
  // asserted away here rather than defaulted — a default would change what the modal renders.
  const deriveConditionsFromObject = (obj?: Record<string, FilterConditionEntry>): FilterCondition[] => {
    if (!obj) return [];
    return Object.values(obj).map((c) => ({
      field: c?.id!,
      operator: c?.option!,
      value: c?.text as FilterCondition["value"],
      logic: c?.logic || "and",
      id: c?.id,
    }));
  };

  const [conditions, setConditions] = useState<FilterCondition[]>(
    deriveConditionsFromObject(initialFilterConditionsObject),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<string[]>(
    initialFilterConditionsObject ? extractFilterIds(initialFilterConditionsObject) : [],
  );
  const [filterConditionsObject, setFilterConditionsObject] = useState<Record<string, FilterConditionEntry>>(
    initialFilterConditionsObject ?? {},
  );
  const [filterPredicates, setFilterPredicates] = useState<unknown>("");
  const [tempCondition, setTempCondition] = useState<FilterCondition | null>(null);
  // KNOWN BUG, preserved verbatim: initialised to `{ current: null }`, so `.current` is an object
  // WRAPPING another `current` until the grid replaces it. The initialiser is left untouched.
  const internalGridRef = useRef<unknown>({ current: null });
  const gridRef = externalGridRef || internalGridRef;
  const seededConditionsRef = useRef<Record<string, FilterConditionEntry> | null>(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    const obj = initialFilterConditionsObject ?? {};
    seededConditionsRef.current = obj;
    setFilterConditionsObject(obj);
    setConditions(deriveConditionsFromObject(initialFilterConditionsObject));
    setAppliedFilters(extractFilterIds(obj));
  }, [initialFilterConditionsObject]);

  // Propagate filter changes to the consumer
  useEffect(() => {
    if (seededConditionsRef.current === filterConditionsObject) {
      seededConditionsRef.current = null;
      return;
    }
    onFilterChangeRef.current(filterConditionsObject);
  }, [filterConditionsObject]);

  // Propagate filter predicates to the consumer (remote mode)
  useEffect(() => {
    if (isRemote && onFilterPredicatesChange) {
      onFilterPredicatesChange(filterPredicates);
    }
  }, [filterPredicates]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setTempCondition(null);
    setIsOpen(false);
  }, []);

  const columnDefinitions = useMemo(
    () =>
      columns.map((col) => ({
        field: col.field,
        // The modal's column shape brands `name` as translated; callers already hand this component
        // display names they own, so it is re-branded here rather than re-translated.
        name: col.name as TranslatedString,
        filterType: col.filterType,
        id: col.id || col.field,
        disableFiltering: col.disableFiltering,
      })),
    [columns],
  );

  // Local apply filter function — reuses getConditionMet from filterUtils (same as SlickGrid1's applyFilter)
  const applyFilterFunction = useCallback(
    (dataset: TItem[], field: string, operator: string, text: string, filterType: string) => {
      return dataset.filter((item: TItem) => {
        const fieldValue = getFieldValue ? getFieldValue(item, field) : (item as Record<string, unknown>)[field];
        return getConditionMet(filterType as FilterType, fieldValue, operator as QueryOperator, text);
      });
    },
    [getFieldValue],
  );

  // Remote call handler — reuses callRemoteSource from filterUtils (same as SlickGrid1's callRemote)
  // `callRemoteSource` cannot infer a result type from a plain URL string, so the response shape the
  // modal expects is stated here instead.
  const callRemote = useCallback(
    async (_source: string, body: { value: FilterQuery }): Promise<ApiResponse> => {
      if (!remoteDataSource) return { result: [] };
      return callRemoteSource(remoteDataSource.url, body) as Promise<ApiResponse>;
    },
    [remoteDataSource],
  );

  const hasActiveFilters = conditions.length > 0;

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: handleClick, hasActiveFilters })
      ) : (
        <ToolTipComponent content={t("Filtering")}>
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleClick} iconButton={true}>
            <IconComponent name="Filter" width="1.5rem" height="1.5rem" />
            {hasActiveFilters && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  insetInlineEnd: 0,
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "6.25rem",
                  background: "var(--Notification-notification-primary, #6BBBE3)",
                  transform: "translate(-50%, 50%)",
                  zIndex: 10,
                }}
              />
            )}
          </ButtonComponent>
        </ToolTipComponent>
      )}

      {isOpen && (
        <FilterModal
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          columnDefinitions={columnDefinitions}
          conditions={conditions}
          setConditions={setConditions}
          remoteDataSource={isRemote ? (remoteDataSource!.url as string) : null}
          callRemote={callRemote}
          // KNOWN BUG, preserved verbatim: the fallback is the identity function, so a remote source
          // that supplies no `beforeDataBound` hands the modal the whole response envelope where it
          // expects the row array — the modal then iterates a non-array. Only the type is stated here.
          beforeDataBound={remoteDataSource?.beforeDataBound || ((d: unknown) => d as unknown[])}
          datasetForFetchingOptions={data}
          query={isRemote ? remoteDataSource!.query || null : null}
          localData={!isRemote}
          getUpdatedFilterConditions={
            remoteDataSource?.getUpdatedFilterConditions || ((obj: Record<string, unknown>) => obj)
          }
          applyFilterFunction={applyFilterFunction}
          gridRef={gridRef}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
          setFilterPredicates={setFilterPredicates}
          setFilterConditionsObject={setFilterConditionsObject}
          tempCondition={tempCondition}
          setTempCondition={setTempCondition}
        />
      )}
    </>
  );
}
